import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardMedia,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { analytics, WrapperStyles, getUsername } from "../../shared/utils";
import type { FBOrg, FBUser } from "../../shared/types"; // Changed FBTeam to FBGroup
import { fetchUser, fetchOrg, fetchOrgOwner } from "../../shared/services"; // Changed fetchTeam to fetchGroup

import DownloadOverlay from "../overlays/DownloadOverlay";
import FooterOverlay from "../overlays/FooterOverlay";
import LoadingOverlay from "../overlays/LoadingOverlay";
import DynamicMeta from "../shared/DynamicMeta";

const PublicOrgView = () => {
  const { id: orgId } = useParams(); // Changed teamId to orgId
  const [loading, setLoading] = useState<boolean>(true);
  const [org, setOrg] = useState<FBOrg>();
  const [user, setUser] = useState<FBUser>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Fetch referrer id if passed
  const [searchParams] = useSearchParams();
  const referrerId = searchParams.get("referrerId");

  useEffect(() => {
    analytics.screen("Web/Public/Org", {
      // Changed Team to Org
      orgId: orgId, // Changed teamId to orgId
      referrerId: referrerId,
    });
  }, [orgId, referrerId]); // Changed teamId to orgId

  useEffect(() => {
    if (!orgId) {
      // Changed teamId to orgId
      setLoading(false);
      return;
    }
    setLoading(true);

    // Conditionally fetch user based on referrerId
    const userPromise = referrerId
      ? fetchUser(referrerId)
      : fetchOrgOwner(orgId); // Changed fetchTeamOwner to fetchGroupOwner

    Promise.all([fetchOrg(orgId), userPromise])
      .then(([org, user]) => {
        // Changed team to org
        setOrg(org); // Changed setTeam to setOrg
        setUser(user);
      })
      .catch((error) => {
        console.error(error);
        setUser(undefined);
        setOrg(undefined); // Changed setTeam to setOrg
      })
      .finally(() => setLoading(false));
  }, [orgId, referrerId]); // Changed teamId to orgId

  const OrgOverlay = () => {
    // Changed TeamOverlay to OrgOverlay
    if (!user || !org) return null; // Changed team to org
    const name = getUsername(user);
    return (
      <Card
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          gap: 1,
          width: "100% !important",
          borderRadius: 2,
        }}
      >
        <CardContent
          sx={{
            flex: "1 0 auto",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "row",
            backgroundColor: "rgba(0, 0, 0, 0.87)",
            borderRadius: 2,
            width: "100% !important",
          }}
        >
          <Box
            sx={{
              justifyContent: "center",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            {user.profilePicture && (
              <Avatar
                src={user.profilePicture}
                alt={name}
                sx={{ width: 48, height: 48 }}
              />
            )}
            <Typography
              sx={{
                color: "white",
                fontSize: !isMobile ? 18 : 16,
                fontWeight: "bold",
                textAlign: "center",
                numberOfLines: 2,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {name} invited you to join their circuit
            </Typography>
            {org.logo && ( // Changed team.logo to org.logo
              <CardMedia
                component="img"
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 2,
                  objectFit: "cover",
                }}
                image={org.logo}
                alt="Organization logo"
              />
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const getMetaTitle = () => {
    if (!org) return "Try Hooper today - download from the app store";
    const name = (user && getUsername(user)) || "Someone";
    const orgName = org.name || "their circuit";
    return `${name} invited you to join ${orgName} on Hooper`;
  };

  const getMetaDescription = () => {
    if (!org) return "Create your stats and highlights, shot by shot"; // Changed team to org
    const name = (user && getUsername(user)) || "someone";
    const orgName = org.name || "their circuit";
    return `Join ${name}'s circuit "${orgName}" on Hooper and start tracking your basketball stats and highlights together.`;
  };

  const getMetaImage = () => {
    if (org?.logo) return org.logo;
    if (user?.profilePicture) return user.profilePicture;
    return "/logos/logo-white.svg";
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        minHeight: "100vh",
        flexDirection: "column",
        display: "flex",
        px: "0 !important",
      }}
    >
      <DynamicMeta
        title={getMetaTitle()}
        description={getMetaDescription()}
        image={getMetaImage()}
        type="profile"
        url={`/hoopers/org/${orgId}${referrerId ? `?referrerId=${referrerId}` : ""}`} // Changed team to org, teamId to orgId
      />
      <Box sx={WrapperStyles}>
        <Box style={{ marginTop: 100 }} />
        {loading ? (
          <LoadingOverlay />
        ) : (
          <DownloadOverlay
            parentViewName="Org"
            backgroundId="background-5.jpg"
            deepLinkPath={`/hoopers/org/${orgId}${referrerId ? `?referrerId=${referrerId}` : ""}`}
            children={<OrgOverlay />}
          />
        )}
        <Box sx={{ my: 8 }} />
        <FooterOverlay />
      </Box>
    </Container>
  );
};

export default PublicOrgView;
